import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatIDR, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import RupiahInput from "@/components/RupiahInput";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Printer, Money } from "@phosphor-icons/react";

const badgeColorForDays = (days) => {
  if (days <= 0) return { bg: "#10B981", label: "Belum Jatuh Tempo" };
  if (days <= 7) return { bg: "#F59E0B", label: `Telat ${days} hari` };
  if (days <= 30) return { bg: "#F97316", label: `Telat ${days} hari` };
  return { bg: "#E63946", label: `Telat ${days} hari` };
};

// terbilang Rupiah singkat: converts number to Indonesian words
const angka = ["", "satu", "dua", "tiga", "empat", "lima", "enam", "tujuh", "delapan", "sembilan", "sepuluh", "sebelas"];
function terbilang(n) {
  n = Math.floor(Math.abs(Number(n) || 0));
  if (n < 12) return angka[n] || "nol";
  if (n < 20) return terbilang(n - 10) + " belas";
  if (n < 100) return terbilang(Math.floor(n / 10)) + " puluh " + terbilang(n % 10);
  if (n < 200) return "seratus " + terbilang(n - 100);
  if (n < 1000) return terbilang(Math.floor(n / 100)) + " ratus " + terbilang(n % 100);
  if (n < 2000) return "seribu " + terbilang(n - 1000);
  if (n < 1000000) return terbilang(Math.floor(n / 1000)) + " ribu " + terbilang(n % 1000);
  if (n < 1000000000) return terbilang(Math.floor(n / 1000000)) + " juta " + terbilang(n % 1000000);
  return terbilang(Math.floor(n / 1000000000)) + " milyar " + terbilang(n % 1000000000);
}
const terbilangRupiah = (n) => (terbilang(n).replace(/\s+/g, " ").trim() + " rupiah").replace(/^./, (c) => c.toUpperCase());

export default function SaleDetail() {
  const { id } = useParams();
  const [sale, setSale] = useState(null);
  const [payments, setPayments] = useState([]);
  const [settings, setSettings] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [selectedInst, setSelectedInst] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [receiptFor, setReceiptFor] = useState(null); // 'sale' | { paymentId }
  const [saleReceiptOpen, setSaleReceiptOpen] = useState(false);
  const [paymentReceipt, setPaymentReceipt] = useState(null);

  const load = async () => {
    const s = await api.get(`/sales/${id}`);
    setSale(s.data);
    const p = await api.get(`/sales/${id}/payments`);
    setPayments(p.data);
  };

  useEffect(() => {
    load();
    api.get("/settings").then((r) => setSettings(r.data));
  }, [id]);

  const openPay = (inst) => {
    setSelectedInst(inst);
    setPayAmount(String(Math.max(0, inst.amount - (inst.paid_amount || 0)).toFixed(0)));
    setPayOpen(true);
  };

  const submitPay = async () => {
    try {
      await api.post(`/sales/${id}/payments`, {
        installment_id: selectedInst.id,
        amount: parseFloat(payAmount),
        payment_date: payDate,
      });
      toast.success("Pembayaran berhasil dicatat");
      setPayOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mencatat pembayaran");
    }
  };

  const printSaleReceipt = () => { setSaleReceiptOpen(true); setTimeout(() => window.print(), 300); };
  const printPaymentReceipt = (payment, inst) => {
    setPaymentReceipt({ payment, inst });
    setTimeout(() => window.print(), 300);
  };

  if (!sale) return <div className="p-10 text-sm text-gray-500">Memuat...</div>;

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const totalPaid = (sale.installments || []).reduce((a, b) => a + (b.paid_amount || 0), 0);
  const totalDue = (sale.installments || []).reduce((a, b) => a + b.amount, 0);
  const periodLabel = sale.payment_period_months === 12 ? "Tahun" : (sale.payment_period_months + " Bulan");

  return (
    <div>
      <div className="no-print border-b border-gray-200 px-10 py-6 flex items-center justify-between bg-white">
        <div className="flex items-center gap-4">
          <Link to="/sales" className="text-xs uppercase tracking-wider text-gray-600 hover:text-black flex items-center gap-1">
            <ArrowLeft size={12} /> Kembali
          </Link>
          <div className="h-4 border-l border-gray-300" />
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-gray-400 font-medium">No. Rekening</div>
            <div className="font-display text-2xl font-bold font-mono-num">{sale.account_number || "-"}</div>
            <div className="text-xs text-gray-500 mt-1">{sale.customer?.name}</div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={printSaleReceipt} data-testid="print-sale" className="rounded-none bg-white border border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white uppercase tracking-wider text-xs h-10">
            <Printer size={14} className="mr-2" /> Kwitansi Penjualan
          </Button>
        </div>
      </div>

      <div className="p-10 no-print">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border border-gray-200 -m-px mb-8">
          <div className="border border-gray-200 p-6">
            <div className="text-[10px] uppercase tracking-widest text-gray-500">Pelanggan</div>
            <div className="mt-2 font-semibold">{sale.customer?.name}</div>
            <div className="text-xs text-gray-500">{sale.customer?.phone}</div>
          </div>
          <div className="border border-gray-200 p-6">
            <div className="text-[10px] uppercase tracking-widest text-gray-500">Mobil</div>
            <div className="mt-2 font-semibold">{sale.car?.brand} {sale.car?.model}</div>
            <div className="text-xs text-gray-500">{sale.car?.type ? sale.car.type + " · " : ""}{sale.car?.year} · {sale.car?.plate_number}</div>
          </div>
          <div className="border border-gray-200 p-6">
            <div className="text-[10px] uppercase tracking-widest text-gray-500">Makelar / Sales</div>
            <PetugasEditor sale={sale} onSaved={load} />
          </div>
          <div className="border border-gray-200 p-6">
            <div className="text-[10px] uppercase tracking-widest text-gray-500">Harga</div>
            <div className="mt-2 font-mono-num font-semibold text-lg">{formatIDR(sale.total_price)}</div>
            {sale.sale_type === "credit" && <div className="text-xs text-gray-500 font-mono-num">DP {formatIDR(sale.down_payment)}</div>}
          </div>
        </div>

        {sale.sale_type === "credit" && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-gray-200 -m-px mb-8">
              <div className="border border-gray-200 p-6">
                <div className="text-[10px] uppercase tracking-widest text-gray-500">Tenor</div>
                <div className="mt-2 font-mono-num font-semibold">{sale.tenor_months} bulan · setiap {sale.payment_period_months} bln</div>
                <div className="text-xs text-gray-500">Bunga {sale.annual_rate}% / tahun</div>
              </div>
              <div className="border border-gray-200 p-6">
                <div className="text-[10px] uppercase tracking-widest text-gray-500">Total Angsuran</div>
                <div className="mt-2 font-mono-num font-semibold text-lg">{formatIDR(totalDue)}</div>
              </div>
              <div className="border border-gray-200 p-6">
                <div className="text-[10px] uppercase tracking-widest text-gray-500">Sudah Terbayar</div>
                <div className="mt-2 font-mono-num font-semibold text-lg">{formatIDR(totalPaid)}</div>
                <div className="text-xs text-gray-500 font-mono-num">Sisa {formatIDR(totalDue - totalPaid)}</div>
              </div>
            </div>

            <h2 className="font-display text-xl font-semibold mb-3">Jadwal Cicilan</h2>
            <div className="border border-gray-200 mb-10 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left text-[10px] uppercase tracking-widest text-gray-600">
                    <th className="p-4">#</th>
                    <th className="p-4">Jatuh Tempo</th>
                    <th className="p-4 text-right">Jumlah</th>
                    <th className="p-4 text-right">Terbayar</th>
                    <th className="p-4">Status</th>
                    <th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {(sale.installments || []).map((inst) => {
                    const due = new Date(inst.due_date + "T00:00:00");
                    const daysLate = Math.floor((today - due) / (1000 * 60 * 60 * 24));
                    const b = inst.status === "paid"
                      ? { bg: "#10B981", label: "LUNAS" }
                      : badgeColorForDays(daysLate);
                    return (
                      <tr key={inst.id} className="border-b border-gray-100" data-testid={`inst-row-${inst.sequence}`}>
                        <td className="p-4 font-mono-num">{inst.sequence}</td>
                        <td className="p-4 font-mono-num text-xs">{formatDate(inst.due_date)}</td>
                        <td className="p-4 text-right font-mono-num">{formatIDR(inst.amount)}</td>
                        <td className="p-4 text-right font-mono-num">{formatIDR(inst.paid_amount || 0)}</td>
                        <td className="p-4">
                          <span className="inline-block px-2 py-1 text-[10px] uppercase tracking-wider text-white font-semibold" style={{ backgroundColor: b.bg }}>
                            {b.label}
                          </span>
                        </td>
                        <td className="p-4 text-right">
                          {inst.status !== "paid" && (
                            <Button onClick={() => openPay(inst)} data-testid={`pay-btn-${inst.sequence}`} className="h-8 rounded-none bg-[#0A0A0A] hover:bg-gray-800 text-white uppercase tracking-wider text-[10px]">
                              <Money size={12} className="mr-1" /> Bayar
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <h2 className="font-display text-xl font-semibold mb-3">Riwayat Pembayaran</h2>
            <div className="border border-gray-200 overflow-x-auto overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left text-[10px] uppercase tracking-widest text-gray-600">
                    <th className="p-4">Tanggal</th>
                    <th className="p-4">Angsuran #</th>
                    <th className="p-4 text-right">Jumlah</th>
                    <th className="p-4">Diterima Oleh</th>
                    <th className="p-4 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => {
                    const inst = (sale.installments || []).find((i) => i.id === p.installment_id);
                    return (
                      <tr key={p.id} className="border-b border-gray-100">
                        <td className="p-4 font-mono-num text-xs">{formatDate(p.payment_date)}</td>
                        <td className="p-4 font-mono-num">#{inst?.sequence || "-"}</td>
                        <td className="p-4 text-right font-mono-num">{formatIDR(p.amount)}</td>
                        <td className="p-4 text-xs text-gray-600">{p.received_by}</td>
                        <td className="p-4 text-right">
                          <button onClick={() => printPaymentReceipt(p, inst)} data-testid={`print-payment-${p.id}`} className="text-xs uppercase tracking-wider text-gray-600 hover:text-black inline-flex items-center gap-1">
                            <Printer size={12} /> Kwitansi
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {payments.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-gray-500 text-sm">Belum ada pembayaran</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* ======== PRINT: Kwitansi Sementara (sale) ======== */}
      {settings && saleReceiptOpen && (
        <div className="print-only" onAnimationEnd={() => setSaleReceiptOpen(false)}>
          <div className="flex items-start gap-4">
            {settings.logo_url && <img src={settings.logo_url} alt="Logo" className="w-20 h-20 object-contain" />}
            <div className="flex-1"><ReceiptHeader s={settings} /></div>
          </div>
          <div className="mt-4 flex justify-end">
            <div className="text-sm">Rek. K. <span className="border-b border-black inline-block min-w-[120px] pl-2 font-mono-num">{sale.account_number}</span></div>
          </div>
          <h1 className="text-center font-bold text-2xl mt-4 underline" style={{ fontFamily: "serif" }}>KWITANSI SEMENTARA</h1>
          <div className="mt-8 space-y-3 text-[14px]">
            <ReceiptRow label="Kendaraan" value={`${sale.car?.brand || ""} ${sale.car?.model || ""} ${sale.car?.type || ""}`} />
            <ReceiptRow label="Nomor Polisi" value={sale.car?.plate_number || "-"} />
            <ReceiptRow label="Uang Muka" value={`Rp. ${formatIDR(sale.down_payment || 0).replace("Rp","").trim()}`} />
            <ReceiptRow label="Dibayar" value={`Rp. ${formatIDR(sale.total_price).replace("Rp","").trim()}`} />
            <div className="pt-3 flex items-center gap-4">
              <div>Sisa uang muka tanggal :</div>
              <div className="border border-black px-3 py-1 min-w-[240px] font-mono-num">Rp.</div>
            </div>
            <div>Angsuran tiap-tiap <span className="inline-block border-b border-black min-w-[80px] text-center font-mono-num">{sale.payment_period_months}</span> {sale.payment_period_months >= 12 ? "Tahun" : "Bulan"} @ Rp. <span className="inline-block border-b border-black min-w-[180px] text-center font-mono-num">{formatIDR((sale.installments && sale.installments[0]?.amount) || 0).replace("Rp","").trim()}</span></div>
            <div>Selama <span className="inline-block border-b border-black min-w-[80px] text-center font-mono-num">{(sale.installments || []).length}</span> {sale.payment_period_months >= 12 ? "Tahun" : "Bulan"}</div>
          </div>
          <div className="mt-8 text-right text-sm">{settings.city}, Tgl <span className="inline-block border-b border-black min-w-[140px] text-center font-mono-num">{formatDate(sale.sale_date)}</span></div>
          <Signatories s={settings} names={{ 1: sale.customer?.name || "" }} />
        </div>
      )}

      {/* ======== PRINT: Kwitansi Tanda Terima (payment) ======== */}
      {settings && paymentReceipt && (
        <div className="print-only" onAnimationEnd={() => setPaymentReceipt(null)}>
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-3">
              {settings.logo_url && <img src={settings.logo_url} alt="Logo" className="w-16 h-16 object-contain" />}
              <div className="font-bold text-3xl" style={{ fontFamily: "serif" }}>KWITANSI</div>
            </div>
            <div className="text-right">
              <div className="text-sm">No. <span className="font-mono-num font-semibold">{sale.account_number}</span></div>
              <div className="text-sm">REK: <span className="font-mono-num">{paymentReceipt.inst?.sequence || "-"}</span></div>
            </div>
          </div>
          <div className="mt-3 border-t border-black" />
          <ReceiptHeader s={settings} compact />
          <h2 className="text-center font-bold text-xl mt-4 underline" style={{ fontFamily: "serif" }}>TANDA TERIMA</h2>
          <div className="mt-6 text-[14px]">Telah terima pembayaran uang :</div>
          <div className="mt-4 space-y-3 text-[14px]">
            <ReceiptRow label="Dari" value={sale.customer?.name || "-"} />
            <ReceiptRow label="Alamat" value={sale.customer?.address || "-"} />
            <ReceiptRow label="Kendaraan/Th" value={`${sale.car?.brand || ""} ${sale.car?.model || ""} / ${sale.car?.year || ""}`} />
            <ReceiptRow label="No. Polisi" value={sale.car?.plate_number || "-"} />
            <ReceiptRow label="Angsuran Ke" value={String(paymentReceipt.inst?.sequence || "-")} />
            <ReceiptRow label="Sebesar" value={`Rp. ${formatIDR(paymentReceipt.payment.amount).replace("Rp","").trim()}`} />
          </div>
          <div className="mt-3 text-[13px] italic"># {terbilangRupiah(paymentReceipt.payment.amount)} #</div>
          <div className="mt-8 text-right text-sm">{settings.city}, Tgl <span className="inline-block border-b border-black min-w-[140px] text-center font-mono-num">{formatDate(paymentReceipt.payment.payment_date)}</span></div>
          <Signatories s={settings} />
        </div>
      )}

      {/* Payment Dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="rounded-none max-w-[95vw] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Catat Pembayaran</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="text-sm">
              Angsuran #{selectedInst?.sequence} · Jatuh tempo {selectedInst && formatDate(selectedInst.due_date)}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Jumlah Bayar</Label>
              <RupiahInput value={payAmount} onChange={(e) => setPayAmount(e.target.value)} className="mt-2 rounded-none" data-testid="pay-amount-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Tanggal</Label>
              <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} className="mt-2 rounded-none" data-testid="pay-date-input" />
            </div>
            <Button onClick={submitPay} data-testid="pay-submit" className="w-full rounded-none bg-[#0A0A0A] hover:bg-gray-800 text-white uppercase tracking-wider text-xs h-11">
              Simpan Pembayaran
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const ReceiptRow = ({ label, value }) => (
  <div className="flex items-baseline gap-3">
    <div className="w-[140px] shrink-0">{label}</div>
    <div className="shrink-0">:</div>
    <div className="flex-1 border-b border-dotted border-black min-h-[20px] font-medium">{value}</div>
  </div>
);

function PetugasEditor({ sale, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(sale.makelar_name || "");
  const [comm, setComm] = useState(String(sale.makelar_commission || 0));

  useEffect(() => {
    setName(sale.makelar_name || "");
    setComm(String(sale.makelar_commission || 0));
  }, [sale.makelar_name, sale.makelar_commission]);

  const save = async () => {
    try {
      await api.put(`/sales/${sale.id}/petugas`, { makelar_name: name, makelar_commission: parseFloat(comm || 0) });
      toast.success("Makelar diperbarui");
      setEditing(false);
      onSaved && onSaved();
    } catch (e) { toast.error("Gagal menyimpan"); }
  };

  if (editing) {
    return (
      <div className="mt-2 space-y-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nama makelar" className="rounded-none h-9 text-sm" data-testid="makelar-name-input" autoFocus />
        <RupiahInput value={comm} onChange={(e) => setComm(e.target.value)} className="rounded-none h-9" data-testid="makelar-comm-input" />
        <div className="flex gap-2">
          <button onClick={save} data-testid="makelar-save" className="text-xs uppercase tracking-wider bg-[#0A0A0A] text-white px-3 py-2 hover:bg-gray-800">Simpan</button>
          <button onClick={() => setEditing(false)} className="text-xs uppercase tracking-wider text-gray-500 hover:text-black">Batal</button>
        </div>
      </div>
    );
  }
  return (
    <div className="mt-2">
      <div className="font-semibold">{sale.makelar_name || <span className="text-gray-400 italic font-normal">Tanpa makelar</span>}</div>
      {sale.makelar_commission > 0 && <div className="text-xs text-gray-500 font-mono-num mt-1">Komisi {formatIDR(sale.makelar_commission)}</div>}
      <button onClick={() => setEditing(true)} data-testid="petugas-edit-btn" className="mt-2 text-xs uppercase tracking-wider text-gray-500 hover:text-black underline">
        {sale.makelar_name ? "Ubah" : "Tetapkan"}
      </button>
    </div>
  );
}

const ReceiptHeader = ({ s, compact }) => (
  <div className={compact ? "text-center" : ""}>
    <div className="font-bold text-xl" style={{ fontFamily: "serif" }}>{s.name}</div>
    <div className="text-xs mt-1 leading-relaxed">
      {s.address_office && <div>Kantor: {s.address_office} · Telp {s.phone_office}</div>}
      {s.address_home && <div>Rumah: {s.address_home} · Telp {s.phone_home}</div>}
    </div>
  </div>
);

const Signatories = ({ s, names = {} }) => (
  <div className="mt-10 grid grid-cols-3 gap-6 text-center text-[13px]">
    {[1, 2, 3].map((n) => (
      <div key={n}>
        <div className="italic">{s[`signatory${n}_label`] || `Penerima ${n}`}</div>
        <div className="h-16" />
        <div className="border-t border-black pt-1 font-semibold">
          {names[n] ? `( ${names[n]} )` : `( ${s[`signatory${n}_name`] || "____________"} )`}
        </div>
      </div>
    ))}
  </div>
);
